/**
 * Fuzzy Form Mapping Utility
 *
 * This utility provides intelligent form mapping to ensure the correct
 * form information goes to the right place, even when there are slight
 * variations in form types or configurations.
 */

import { getWTFFormsConfig } from "../components/env";

const isFuzzyMappingDebugEnabled = () =>
  typeof window !== "undefined" &&
  window.localStorage?.getItem("debug:fuzzy-form-mapping") === "true";

const fuzzyDebugLog = (...args) => {
  if (isFuzzyMappingDebugEnabled()) {
    console.log(...args);
  }
};

// Form type mappings with fuzzy matching capabilities
export const FORM_TYPE_MAPPINGS = {
  // New Pulmonary Patient forms
  NP: {
    primary: "NP",
    aliases: ["NEW_PULMONARY", "PULMONARY_NEW", "PULM_NEW", "NEW_PULM"],
    description: "New Pulmonary Patient",
    formId: "bb76a841-4239-4e14-a0e7-3585cab4f78c",
    messageTemplate: "New Pulmonary Patient forms",
  },

  // New Sleep Patient forms
  NS: {
    primary: "NS",
    aliases: ["NEW_SLEEP", "SLEEP_NEW", "SLEEP_PATIENT", "NEW_SLEEP_PATIENT"],
    description: "New Sleep Patient",
    formId: "991de856-cc1e-4d99-8584-22f5a61053c8",
    messageTemplate: "New Sleep Patient forms",
  },

  // Follow-up Pulmonary Patient forms
  FFP: {
    primary: "FFP",
    aliases: [
      "FOLLOW_UP_PULMONARY",
      "PULMONARY_FOLLOWUP",
      "PULM_FOLLOWUP",
      "FOLLOWUP_PULM",
    ],
    description: "Follow-up Pulmonary Patient",
    formId: "", // No fixed form ID - uses existing follow-up forms
    messageTemplate: "Follow-up Pulmonary Patient forms",
  },

  // Follow-up Sleep Patient forms
  FFS: {
    primary: "FFS",
    aliases: [
      "FOLLOW_UP_SLEEP",
      "SLEEP_FOLLOWUP",
      "SLEEP_FOLLOW",
      "FOLLOWUP_SLEEP",
    ],
    description: "Follow-up Sleep Patient",
    formId: "", // No fixed form ID - uses existing follow-up forms
    messageTemplate: "Follow-up Sleep Patient forms",
  },

  // Clinical Form 1
  CL1: {
    primary: "CL1",
    aliases: ["CLINICAL_1", "CLINICAL_FORM_1", "CLINICAL_ONE", "CL_1"],
    description: "Clinical Form 1",
    formId: "3dc81e20-d069-475b-a94e-76bd615c6e26",
    messageTemplate: "Patient Intake forms",
  },

  // Clinical Form 2
  CL2: {
    primary: "CL2",
    aliases: ["CLINICAL_2", "CLINICAL_FORM_2", "CLINICAL_TWO", "CL_2"],
    description: "Clinical Form 2",
    formId: "953b1782-45b1-4161-b90d-a4dad57f0e9a",
    messageTemplate: "Patient Intake forms",
  },
};

// Helper function to get environment-specific form URL
const getEnvironmentFormUrl = (formId) => {
  const config = getWTFFormsConfig();
  const url = `${config.baseURL}${config.formsPath}/${formId}`;
  fuzzyDebugLog("[ENVIRONMENT FORM URL] Constructed URL:", {
    hostname: window.location.hostname,
    config,
    formId,
    finalUrl: url,
  });
  return url;
};

// Helper function to create URL to form type mapping dynamically
const createUrlToFormTypeMapping = () => {
  const mapping = {};
  const config = getWTFFormsConfig();

  for (const [formType, formConfig] of Object.entries(FORM_TYPE_MAPPINGS)) {
    const url = getEnvironmentFormUrl(formConfig.formId);
    mapping[url] = formType;
  }

  // Add legacy URLs for backward compatibility
  const legacyMappings = {
    "3affae16-9e82-4249-982d-14f329400673": "NP", // New NP form ID
    "406144f6-5224-4105-9f57-dcb79a808ca4": "NS", // NS form ID
    "3dc81e20-d069-475b-a94e-76bd615c6e26": "CL1", // CL1 form ID
    "953b1782-45b1-4161-b90d-a4dad57f0e9a": "CL2", // CL2 form ID
    "ebcc3ccc-d0e0-4663-a78b-b9a29ed14a18": "NP", // Legacy NP form ID
  };

  for (const [legacyFormId, formType] of Object.entries(legacyMappings)) {
    const legacyUrl = `${config.baseURL}${config.formsPath}/${legacyFormId}`;
    mapping[legacyUrl] = formType;
  }

  return mapping;
};

// URL to form type mapping for reverse lookup (created dynamically)
export const URL_TO_FORM_TYPE_MAPPING = createUrlToFormTypeMapping();

/**
 * Fuzzy match a form type string to the correct form type
 * @param {string} input - The input form type string
 * @returns {string|null} - The matched form type or null if no match
 */
export const fuzzyMatchFormType = (input) => {
  if (!input || typeof input !== "string") return null;

  const normalizedInput = input.toUpperCase().trim();

  // Direct match first
  if (FORM_TYPE_MAPPINGS[normalizedInput]) {
    return normalizedInput;
  }

  // Fuzzy match against aliases
  for (const [formType, config] of Object.entries(FORM_TYPE_MAPPINGS)) {
    // Check if input matches any alias
    if (
      config.aliases.some(
        (alias) =>
          alias.toUpperCase() === normalizedInput ||
          alias.toUpperCase().includes(normalizedInput) ||
          normalizedInput.includes(alias.toUpperCase()),
      )
    ) {
      return formType;
    }

    // Check if input contains the form type
    if (
      normalizedInput.includes(formType) ||
      formType.includes(normalizedInput)
    ) {
      return formType;
    }
  }

  return null;
};

/**
 * Get form configuration by type with fuzzy matching
 * @param {string} formType - The form type to look up
 * @returns {object|null} - The form configuration or null if not found
 */
export const getFormConfig = (formType) => {
  const matchedType = fuzzyMatchFormType(formType);
  if (!matchedType) return null;

  return {
    type: matchedType,
    ...FORM_TYPE_MAPPINGS[matchedType],
  };
};

/**
 * Get form URL by type with fuzzy matching and user settings
 * @param {string} formType - The form type
 * @param {object} userSettings - User's custom form URLs
 * @returns {string} - The form URL
 */
export const getFormUrl = (formType, userSettings = {}) => {
  fuzzyDebugLog("[FUZZY MAPPING] getFormUrl called with:", {
    formType,
    userSettings,
  });

  const config = getFormConfig(formType);
  if (!config) {
    fuzzyDebugLog("[FUZZY MAPPING] No config found for formType:", formType);
    return "";
  }

  fuzzyDebugLog("[FUZZY MAPPING] Found config:", config);

  // For FFP/FFS types, return empty string as they use existing follow-up forms
  if (config.type === "FFP" || config.type === "FFS") {
    fuzzyDebugLog(
      `[FUZZY MAPPING] ${config.type} uses existing follow-up forms, no fixed URL`,
    );
    return "";
  }

  // Check for custom URL in user settings (from schema_settings table)
  const customUrl = userSettings[`${config.type}_FORM_URL`];
  if (customUrl) {
    fuzzyDebugLog(
      `[FUZZY MAPPING] Using custom URL for ${config.type} from schema_settings:`,
      customUrl,
    );
    return customUrl;
  }

  // Fall back to default form ID from FORM_TYPE_MAPPINGS
  // This ensures forms work even if schema_settings doesn't have the URL configured
  if (config.formId) {
    const envConfig = getWTFFormsConfig();
    const defaultUrl = `${envConfig.baseURL}${envConfig.formsPath}/${config.formId}`;
    fuzzyDebugLog(
      `[FUZZY MAPPING] No URL in schema_settings for ${config.type}, using default: ${defaultUrl}`,
    );
    return defaultUrl;
  }

  fuzzyDebugLog(
    `[FUZZY MAPPING] No URL found for ${config.type}. ` +
      `Please configure ${config.type}_FORM_URL in the schema_settings table.`,
  );
  return "";
};

/**
 * Get message template for form type
 * @param {string} formType - The form type
 * @returns {string} - The message template
 */
export const getFormMessageTemplate = (formType) => {
  const config = getFormConfig(formType);
  return config ? config.messageTemplate : "Patient Intake forms";
};

/**
 * Validate and normalize form types array
 * @param {Array} formTypes - Array of form types
 * @returns {Array} - Array of normalized form types
 */
export const normalizeFormTypes = (formTypes) => {
  if (!Array.isArray(formTypes)) return [];

  const normalized = formTypes
    .map((type) => fuzzyMatchFormType(type))
    .filter((type) => type !== null);

  // Remove duplicates
  return [...new Set(normalized)];
};

/**
 * Get form type from URL (reverse lookup)
 * @param {string} url - The form URL
 * @returns {string|null} - The form type or null if not found
 */
export const getFormTypeFromUrl = (url) => {
  return URL_TO_FORM_TYPE_MAPPING[url] || null;
};

/**
 * Create form URLs array with fuzzy matching
 * @param {Array} formTypes - Array of form types
 * @param {object} userSettings - User's custom form URLs
 * @returns {Array} - Array of form URLs
 */
export const createFormUrls = (formTypes, userSettings = {}) => {
  const normalizedTypes = normalizeFormTypes(formTypes);

  return normalizedTypes
    .map((type) => getFormUrl(type, userSettings))
    .filter((url) => url !== "");
};

/**
 * Debug function to log form mapping information
 * @param {string} input - The input form type
 * @param {object} userSettings - User settings
 */
export const debugFormMapping = (input, userSettings = {}) => {
  console.log("[FUZZY MAPPING DEBUG]", {
    input,
    matchedType: fuzzyMatchFormType(input),
    config: getFormConfig(input),
    url: getFormUrl(input, userSettings),
    messageTemplate: getFormMessageTemplate(input),
  });
};

// ============================================================================
// FUZZY FIELD MAPPING SYSTEM
// ============================================================================

// Field type mappings with fuzzy matching capabilities
export const FIELD_TYPE_MAPPINGS = {
  // Name fields
  name: {
    primary: "name",
    aliases: [
      "patient_name",
      "full_name",
      "patientname",
      "fullname",
      "patient_name_field",
    ],
    description: "Patient Name",
    commonIndices: [0], // Usually the first field
  },

  // Email fields
  email: {
    primary: "email",
    aliases: [
      "email_address",
      "emailaddress",
      "e_mail",
      "mail",
      "patient_email",
    ],
    description: "Email Address",
    commonIndices: [1], // Usually the second field
  },

  // Phone fields
  phone: {
    primary: "tel_num",
    aliases: [
      "phone",
      "phone_number",
      "phonenumber",
      "telephone",
      "tel",
      "mobile",
      "cell",
    ],
    description: "Phone Number",
    commonIndices: [2], // Usually the third field
  },

  // Date of Birth fields
  dob: {
    primary: "dob",
    aliases: [
      "date_of_birth",
      "dateofbirth",
      "birth_date",
      "birthdate",
      "birthday",
    ],
    description: "Date of Birth",
    commonIndices: [3], // Usually the fourth field
  },

  // Type fields
  type: {
    primary: "type",
    aliases: [
      "patient_type",
      "patienttype",
      "visit_type",
      "visittype",
      "form_type",
    ],
    description: "Patient Type",
    commonIndices: [4], // Usually the fifth field
  },

  // Submission fields
  submissions: {
    primary: "submission_url",
    aliases: [
      "submissions",
      "submissions_array",
      "submission",
      "submissionarray",
    ],
    description: "Submissions",
    commonIndices: [5], // Usually the sixth field
  },

  // EHR ID fields
  ehr_id: {
    primary: "EHR_ID",
    aliases: [
      "ehr_id",
      "ehrid",
      "patient_id",
      "patientid",
      "medical_id",
      "medicalid",
    ],
    description: "EHR ID",
    commonIndices: [6], // Usually the seventh field
  },

  // Physician fields
  physician: {
    primary: "physician_name",
    aliases: [
      "physician",
      "physicianname",
      "doctor",
      "doctor_name",
      "doctorname",
    ],
    description: "Physician Name",
    commonIndices: [7], // Usually the eighth field
  },
};

/**
 * Fuzzy match a field name to the correct field type
 * @param {string} input - The input field name
 * @returns {string|null} - The matched field type or null if no match
 */
export const fuzzyMatchFieldType = (input) => {
  if (!input || typeof input !== "string") return null;

  const normalizedInput = input.toLowerCase().trim();

  // Direct match first
  if (FIELD_TYPE_MAPPINGS[normalizedInput]) {
    return normalizedInput;
  }

  // Fuzzy match against aliases
  for (const [fieldType, config] of Object.entries(FIELD_TYPE_MAPPINGS)) {
    // Check if input matches any alias
    if (
      config.aliases.some(
        (alias) =>
          alias.toLowerCase() === normalizedInput ||
          alias.toLowerCase().includes(normalizedInput) ||
          normalizedInput.includes(alias.toLowerCase()),
      )
    ) {
      return fieldType;
    }

    // Check if input contains the field type
    if (
      normalizedInput.includes(fieldType) ||
      fieldType.includes(normalizedInput)
    ) {
      return fieldType;
    }
  }

  return null;
};

/**
 * Get field configuration by type with fuzzy matching
 * @param {string} fieldType - The field type to look up
 * @returns {object|null} - The field configuration or null if not found
 */
export const getFieldConfig = (fieldType) => {
  const matchedType = fuzzyMatchFieldType(fieldType);
  if (!matchedType) return null;

  return {
    type: matchedType,
    ...FIELD_TYPE_MAPPINGS[matchedType],
  };
};

/**
 * Find the best field in patient data using fuzzy matching
 * @param {object} patientData - The patient data object
 * @param {string} fieldType - The field type to find (e.g., 'email', 'phone')
 * @returns {string|null} - The field value or null if not found
 */
export const findPatientField = (patientData, fieldType) => {
  if (!patientData || typeof patientData !== "object") return null;

  const config = getFieldConfig(fieldType);
  if (!config) return null;

  // First, try direct field name match
  if (patientData[config.primary]) {
    console.log(
      `[FUZZY FIELD MAPPING] Direct match for ${fieldType}:`,
      config.primary,
    );
    return patientData[config.primary];
  }

  // Try aliases
  for (const alias of config.aliases) {
    if (patientData[alias]) {
      console.log(`[FUZZY FIELD MAPPING] Alias match for ${fieldType}:`, alias);
      return patientData[alias];
    }
  }

  // Try case-insensitive matching
  const normalizedFieldType = fieldType.toLowerCase();
  for (const [key, value] of Object.entries(patientData)) {
    if (
      key.toLowerCase() === normalizedFieldType ||
      key.toLowerCase().includes(normalizedFieldType) ||
      normalizedFieldType.includes(key.toLowerCase())
    ) {
      console.log(
        `[FUZZY FIELD MAPPING] Case-insensitive match for ${fieldType}:`,
        key,
      );
      return value;
    }
  }

  console.log(`[FUZZY FIELD MAPPING] No match found for ${fieldType}`);
  return null;
};

/**
 * Extract patient information using fuzzy field mapping
 * @param {object} patientData - The patient data object
 * @returns {object} - Extracted patient information
 */
export const extractPatientInfo = (patientData) => {
  if (!patientData || typeof patientData !== "object") {
    return {
      name: "",
      email: "",
      phone: "",
      dob: "",
      type: "",
      ehrId: "",
      physician: "",
    };
  }

  const extracted = {
    name: findPatientField(patientData, "name") || "",
    email: findPatientField(patientData, "email") || "",
    phone: findPatientField(patientData, "phone") || "",
    dob: findPatientField(patientData, "dob") || "",
    type: findPatientField(patientData, "type") || "",
    ehrId: findPatientField(patientData, "ehr_id") || "",
    physician: findPatientField(patientData, "physician") || "",
  };

  console.log("[FUZZY FIELD MAPPING] Extracted patient info:", extracted);
  return extracted;
};

/**
 * Debug function to log field mapping information
 * @param {object} patientData - The patient data object
 * @param {string} fieldType - The field type to debug
 */
export const debugFieldMapping = (patientData, fieldType) => {
  console.log("[FUZZY FIELD MAPPING DEBUG]", {
    fieldType,
    matchedType: fuzzyMatchFieldType(fieldType),
    config: getFieldConfig(fieldType),
    foundValue: findPatientField(patientData, fieldType),
    availableFields: Object.keys(patientData || {}),
  });
};
